import { useCallback, useEffect, useState, type FormEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { useOrgStore } from '../../shared/store/org.store.js'
import { getSettings, updateSettings, updateOrderSchedule } from '../../shared/api/settings.js'
import type { SettingsResponse } from '../../shared/api/settings.js'
import { canManageSettings, canViewSettings } from '../../shared/utils/permissions.js'
import { ApiError } from '../../shared/types/api.js'

type PageStatus = 'loading' | 'ready' | 'error' | 'forbidden'

interface SaveState {
  saving: boolean
  success: boolean
  error: string | null
}

interface DayInterval {
  openTime: string
  closeTime: string
}

interface DaySchedule {
  enabled: boolean
  intervals: DayInterval[]
}

type WeekSchedule = Record<number, DaySchedule>

const DAY_ORDER = [1, 2, 3, 4, 5, 6, 0] as const
const DAY_KEYS: Record<number, string> = {
  1: 'mon',
  2: 'tue',
  3: 'wed',
  4: 'thu',
  5: 'fri',
  6: 'sat',
  0: 'sun',
}

const TIMEZONES = [
  'Europe/Kaliningrad',
  'Europe/Moscow',
  'Europe/Samara',
  'Europe/Volgograd',
  'Asia/Yekaterinburg',
  'Asia/Omsk',
  'Asia/Krasnoyarsk',
  'Asia/Irkutsk',
  'Asia/Yakutsk',
  'Asia/Vladivostok',
  'Asia/Magadan',
  'Asia/Kamchatka',
]

function idleState(): SaveState {
  return { saving: false, success: false, error: null }
}

function defaultInterval(): DayInterval {
  return { openTime: '10:00', closeTime: '22:00' }
}

function emptyWeek(): WeekSchedule {
  const days: WeekSchedule = {}
  for (let day = 0; day < 7; day++) {
    days[day] = { enabled: false, intervals: [] }
  }
  return days
}

function buildWeek(settings: SettingsResponse): WeekSchedule {
  const days = emptyWeek()
  for (const entry of settings.orderSchedule?.entries ?? []) {
    const current = days[entry.dayOfWeek]
    if (!current) continue
    current.enabled = true
    current.intervals.push({ openTime: entry.openTime, closeTime: entry.closeTime })
  }
  return days
}

function toErrorMessage(err: unknown, fallback: string): string {
  if (err instanceof ApiError) return err.message || fallback
  return fallback
}

function fieldErrors(err: unknown): Record<string, string> {
  const result: Record<string, string> = {}
  if (err instanceof ApiError && err.details && typeof err.details === 'object') {
    for (const [path, message] of Object.entries(err.details)) {
      if (path === 'location.name') {
        result.locationName = String(message)
      } else if (path.startsWith('organization.') || path.startsWith('location.')) {
        result[path.split('.').pop() ?? path] = String(message)
      } else {
        result[path] = String(message)
      }
    }
  }
  return result
}

export default function SettingsPage() {
  const { t } = useTranslation()
  const org = useOrgStore(s => s.activeOrganization)
  const orgId = org?.id
  const canView = canViewSettings(org?.role)
  const canManage = canManageSettings(org?.role)

  const [status, setStatus] = useState<PageStatus>('loading')
  const [loadError, setLoadError] = useState('')
  const [settings, setSettings] = useState<SettingsResponse | null>(null)
  const [selectedLocationId, setSelectedLocationId] = useState('')

  const [orgName, setOrgName] = useState('')
  const [orgSlug, setOrgSlug] = useState('')
  const [orgDescription, setOrgDescription] = useState('')
  const [pricePrecisionMode, setPricePrecisionMode] = useState('INTEGER_ONLY')
  const [logoUrl, setLogoUrl] = useState('')
  const [coverUrl, setCoverUrl] = useState('')
  const [locationName, setLocationName] = useState('')
  const [address, setAddress] = useState('')
  const [phone, setPhone] = useState('')
  const [email, setEmail] = useState('')
  const [timezone, setTimezone] = useState('Europe/Moscow')
  const [businessFieldErrors, setBusinessFieldErrors] = useState<Record<string, string>>({})
  const [businessSave, setBusinessSave] = useState<SaveState>(idleState())

  const [pickupEnabled, setPickupEnabled] = useState(true)
  const [deliveryEnabled, setDeliveryEnabled] = useState(true)
  const [acceptanceSave, setAcceptanceSave] = useState<SaveState>(idleState())

  const [scheduleConfigured, setScheduleConfigured] = useState(false)
  const [week, setWeek] = useState<WeekSchedule>(emptyWeek)
  const [scheduleFieldError, setScheduleFieldError] = useState('')
  const [scheduleSave, setScheduleSave] = useState<SaveState>(idleState())

  const applySettings = useCallback((data: SettingsResponse) => {
    setSettings(data)
    setSelectedLocationId(data.location?.id ?? '')
    setOrgName(data.organization.name)
    setOrgSlug(data.organization.slug)
    setOrgDescription(data.organization.description ?? '')
    setPricePrecisionMode(data.organization.pricePrecisionMode || 'INTEGER_ONLY')
    setLogoUrl(data.organization.logoUrl ?? '')
    setCoverUrl(data.organization.coverUrl ?? '')
    setLocationName(data.location?.name ?? '')
    setAddress(data.location?.address ?? '')
    setPhone(data.location?.phone ?? '')
    setEmail(data.location?.email ?? '')
    setTimezone(data.location?.timezone ?? data.orderSchedule?.timezone ?? 'Europe/Moscow')
    setPickupEnabled(data.location?.orderAcceptance.pickupEnabled ?? true)
    setDeliveryEnabled(data.location?.orderAcceptance.deliveryEnabled ?? true)
    setScheduleConfigured(data.orderSchedule?.isConfigured ?? false)
    setWeek(buildWeek(data))
    setBusinessFieldErrors({})
    setBusinessSave(idleState())
    setAcceptanceSave(idleState())
    setScheduleFieldError('')
    setScheduleSave(idleState())
  }, [])

  const load = useCallback(async (locationId?: string) => {
    if (!orgId) return
    setStatus('loading')
    setLoadError('')
    try {
      const data = await getSettings(orgId, locationId)
      applySettings(data)
      setStatus('ready')
    } catch (err: unknown) {
      if (err instanceof ApiError && err.status === 403) {
        setStatus('forbidden')
        return
      }
      setLoadError(t('settings.loadError'))
      setStatus('error')
    }
  }, [orgId, applySettings, t])

  useEffect(() => {
    if (!canView) {
      setStatus('forbidden')
      return
    }
    void load()
  }, [canView, load])

  function touchBusiness() {
    setBusinessSave(prev => ({ ...prev, success: false, error: null }))
  }

  function touchAcceptance() {
    setAcceptanceSave(prev => ({ ...prev, success: false, error: null }))
  }

  function touchSchedule() {
    setScheduleFieldError('')
    setScheduleSave(prev => ({ ...prev, success: false, error: null }))
  }

  async function handleBusinessSubmit(ev: FormEvent<HTMLFormElement>) {
    ev.preventDefault()
    if (!orgId || !canManage) return

    const errors: Record<string, string> = {}
    if (!orgName.trim()) errors.name = t('error.fieldRequired')
    if (!orgSlug.trim()) {
      errors.slug = t('error.fieldRequired')
    } else if (!/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(orgSlug.trim())) {
      errors.slug = t('error.slugInvalid')
    }
    if (email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      errors.email = t('error.emailInvalid')
    }
    setBusinessFieldErrors(errors)
    if (Object.keys(errors).length > 0) return

    setBusinessSave({ saving: true, success: false, error: null })
    try {
      const data = await updateSettings(orgId, {
        locationId: selectedLocationId || undefined,
        organization: {
          name: orgName.trim(),
          slug: orgSlug.trim(),
          description: orgDescription.trim() || null,
          logoUrl: logoUrl.trim() || null,
          coverUrl: coverUrl.trim() || null,
          pricePrecisionMode,
        },
        ...(settings?.location
          ? {
              location: {
                name: locationName.trim(),
                address: address.trim() || null,
                phone: phone.trim() || null,
                email: email.trim() || null,
                timezone,
              },
            }
          : {}),
      })
      applySettings(data)
      setBusinessSave({ saving: false, success: true, error: null })
    } catch (err: unknown) {
      const mapped = fieldErrors(err)
      if (err instanceof ApiError && err.status === 409 && err.code === 'CONFLICT') {
        mapped.slug = t('error.slugTaken')
      }
      setBusinessFieldErrors(mapped)
      setBusinessSave({
        saving: false,
        success: false,
        error: toErrorMessage(err, t('settings.saveError')),
      })
    }
  }

  async function handleAcceptanceSubmit(ev: FormEvent<HTMLFormElement>) {
    ev.preventDefault()
    if (!orgId || !canManage || !settings?.location) return

    if (!pickupEnabled && !deliveryEnabled) {
      setAcceptanceSave({ saving: false, success: false, error: t('settings.acceptance.atLeastOne') })
      return
    }

    setAcceptanceSave({ saving: true, success: false, error: null })
    try {
      const data = await updateSettings(orgId, {
        locationId: settings.location.id,
        orderAcceptance: { pickupEnabled, deliveryEnabled },
      })
      applySettings(data)
      setAcceptanceSave({ saving: false, success: true, error: null })
    } catch (err: unknown) {
      setAcceptanceSave({
        saving: false,
        success: false,
        error: toErrorMessage(err, t('settings.saveError')),
      })
    }
  }

  async function handleScheduleSubmit(ev: FormEvent<HTMLFormElement>) {
    ev.preventDefault()
    if (!orgId || !canManage || !settings?.location) return

    const entries = DAY_ORDER.flatMap(day => {
      const current = week[day]
      if (!current?.enabled) return []
      return current.intervals.map(interval => ({
        dayOfWeek: day,
        openTime: interval.openTime,
        closeTime: interval.closeTime,
      }))
    })

    if (scheduleConfigured && entries.length === 0) {
      setScheduleFieldError(t('settings.schedule.noDays'))
      return
    }
    if (entries.some(entry => entry.openTime === entry.closeTime)) {
      setScheduleFieldError(t('settings.schedule.sameTime'))
      return
    }

    setScheduleFieldError('')
    setScheduleSave({ saving: true, success: false, error: null })
    try {
      const data = await updateOrderSchedule(orgId, {
        locationId: settings.location.id,
        configured: scheduleConfigured,
        entries,
      })
      setSettings(prev => prev ? { ...prev, orderSchedule: data } : prev)
      setScheduleSave({ saving: false, success: true, error: null })
    } catch (err: unknown) {
      setScheduleSave({
        saving: false,
        success: false,
        error: toErrorMessage(err, t('settings.saveError')),
      })
    }
  }

  function handleLocationChange(locationId: string) {
    setSelectedLocationId(locationId)
    void load(locationId)
  }

  function toggleDay(day: number, enabled: boolean) {
    setWeek(prev => {
      const current = prev[day]!
      const intervals = enabled && current.intervals.length === 0
        ? [defaultInterval()]
        : current.intervals
      return { ...prev, [day]: { enabled, intervals } }
    })
    touchSchedule()
  }

  function updateInterval(day: number, index: number, patch: Partial<DayInterval>) {
    setWeek(prev => {
      const current = prev[day]!
      const intervals = current.intervals.map((interval, i) =>
        i === index ? { ...interval, ...patch } : interval,
      )
      return { ...prev, [day]: { ...current, intervals } }
    })
    touchSchedule()
  }

  function addInterval(day: number) {
    setWeek(prev => {
      const current = prev[day]!
      return {
        ...prev,
        [day]: { enabled: true, intervals: [...current.intervals, defaultInterval()] },
      }
    })
    touchSchedule()
  }

  function removeInterval(day: number, index: number) {
    setWeek(prev => {
      const current = prev[day]!
      const intervals = current.intervals.filter((_, i) => i !== index)
      return { ...prev, [day]: { enabled: intervals.length > 0, intervals } }
    })
    touchSchedule()
  }

  if (!orgId) return null

  if (!canView || status === 'forbidden') {
    return (
      <section className="page-section">
        <h2>{t('app.settings')}</h2>
        <div className="settings-card">
          <div className="form-error-banner">{t('settings.forbidden')}</div>
        </div>
      </section>
    )
  }

  if (status === 'loading' && !settings) {
    return (
      <section className="page-section">
        <h2>{t('app.settings')}</h2>
        <div className="page-loading">
          <div className="spinner" />
        </div>
      </section>
    )
  }

  if (status === 'error') {
    return (
      <section className="page-section">
        <h2>{t('app.settings')}</h2>
        <div className="settings-card">
          <div className="form-error-banner">{loadError || t('settings.loadError')}</div>
          <button type="button" className="btn btn-primary" onClick={() => void load(selectedLocationId || undefined)}>
            {t('error.retry')}
          </button>
        </div>
      </section>
    )
  }

  if (!settings) return null

  const disabled = !canManage || businessSave.saving
  const acceptanceDisabled = !canManage || acceptanceSave.saving || !settings.location
  const scheduleDisabled = !canManage || scheduleSave.saving || !settings.location

  return (
    <section className="page-section">
      <h2>{t('app.settings')}</h2>
      <p className="page-section-description">{t('settings.subtitle')}</p>

      {!canManage && (
        <div className="form-error-banner settings-readonly-banner">{t('settings.readonlyHint')}</div>
      )}

      {settings.locations.length > 1 && (
        <div className="settings-card settings-location-picker">
          <div className="form-field">
            <label htmlFor="settingsLocation">{t('settings.locationSelector')}</label>
            <select
              id="settingsLocation"
              value={selectedLocationId}
              onChange={e => handleLocationChange(e.target.value)}
              disabled={businessSave.saving || acceptanceSave.saving || scheduleSave.saving}
            >
              {settings.locations.map(location => (
                <option key={location.id} value={location.id}>{location.name}</option>
              ))}
            </select>
          </div>
        </div>
      )}

      <div className="settings-sections">
        <section className="settings-card" aria-labelledby="settingsBusinessTitle">
          <h3 id="settingsBusinessTitle">{t('settings.business.title')}</h3>
          <p className="page-section-description">{t('settings.business.subtitle')}</p>

          {businessSave.success && (
            <div className="form-success-banner" role="status">{t('settings.saved')}</div>
          )}
          {businessSave.error && (
            <div className="form-error-banner">{businessSave.error}</div>
          )}

          <form onSubmit={handleBusinessSubmit} noValidate>
            <div className="form-field">
              <label htmlFor="settingsOrgName">{t('settings.business.orgName')} *</label>
              <input
                id="settingsOrgName"
                type="text"
                value={orgName}
                onChange={e => { setOrgName(e.target.value); touchBusiness() }}
                maxLength={200}
                required
                disabled={disabled}
                autoComplete="organization"
              />
              {businessFieldErrors.name && <span className="field-error">{businessFieldErrors.name}</span>}
            </div>

            <div className="form-field">
              <label htmlFor="settingsOrgSlug">{t('settings.business.slug')} *</label>
              <input
                id="settingsOrgSlug"
                type="text"
                value={orgSlug}
                onChange={e => { setOrgSlug(e.target.value); touchBusiness() }}
                maxLength={100}
                required
                disabled={disabled}
                autoComplete="off"
              />
              <span className="field-hint">{t('settings.business.slugHint')}</span>
              {businessFieldErrors.slug && <span className="field-error">{businessFieldErrors.slug}</span>}
            </div>

            <div className="form-field">
              <label htmlFor="settingsOrgDescription">{t('settings.business.description')}</label>
              <textarea
                id="settingsOrgDescription"
                value={orgDescription}
                onChange={e => { setOrgDescription(e.target.value); touchBusiness() }}
                rows={3}
                maxLength={2000}
                disabled={disabled}
              />
            </div>

            <div className="form-field">
              <label htmlFor="settingsPricePrecision">{t('settings.business.pricePrecision')}</label>
              <select
                id="settingsPricePrecision"
                value={pricePrecisionMode}
                onChange={e => { setPricePrecisionMode(e.target.value); touchBusiness() }}
                disabled={disabled}
              >
                <option value="INTEGER_ONLY">{t('settings.business.pricePrecisionInteger')}</option>
                <option value="HALF_UNIT">{t('settings.business.pricePrecisionHalfUnit')}</option>
              </select>
              <span className="field-hint">{t('settings.business.pricePrecisionHint')}</span>
              {businessFieldErrors.pricePrecisionMode && <span className="field-error">{businessFieldErrors.pricePrecisionMode}</span>}
            </div>

            <div className="form-row">
              <div className="form-field">
                <label htmlFor="settingsLogoUrl">{t('settings.business.logoUrl')}</label>
                <input
                  id="settingsLogoUrl"
                  type="url"
                  value={logoUrl}
                  onChange={e => { setLogoUrl(e.target.value); touchBusiness() }}
                  maxLength={2048}
                  disabled={disabled}
                  placeholder="https://..."
                />
                {businessFieldErrors.logoUrl && <span className="field-error">{businessFieldErrors.logoUrl}</span>}
              </div>

              <div className="form-field">
                <label htmlFor="settingsCoverUrl">{t('settings.business.coverUrl')}</label>
                <input
                  id="settingsCoverUrl"
                  type="url"
                  value={coverUrl}
                  onChange={e => { setCoverUrl(e.target.value); touchBusiness() }}
                  maxLength={2048}
                  disabled={disabled}
                  placeholder="https://..."
                />
                {businessFieldErrors.coverUrl && <span className="field-error">{businessFieldErrors.coverUrl}</span>}
              </div>
            </div>

            {settings.location && (
              <>
                <h4 className="settings-subheading">{t('settings.business.locationSection')}</h4>

                <div className="form-field">
                  <label htmlFor="settingsLocationName">{t('settings.business.locationName')}</label>
                  <input
                    id="settingsLocationName"
                    type="text"
                    value={locationName}
                    onChange={e => { setLocationName(e.target.value); touchBusiness() }}
                    maxLength={200}
                    disabled={disabled}
                  />
                  {businessFieldErrors.locationName && <span className="field-error">{businessFieldErrors.locationName}</span>}
                </div>

                <div className="form-field">
                  <label htmlFor="settingsAddress">{t('settings.business.address')}</label>
                  <input
                    id="settingsAddress"
                    type="text"
                    value={address}
                    onChange={e => { setAddress(e.target.value); touchBusiness() }}
                    maxLength={500}
                    disabled={disabled}
                    autoComplete="street-address"
                  />
                  {businessFieldErrors.address && <span className="field-error">{businessFieldErrors.address}</span>}
                </div>

                <div className="form-row">
                  <div className="form-field">
                    <label htmlFor="settingsPhone">{t('settings.business.phone')}</label>
                    <input
                      id="settingsPhone"
                      type="tel"
                      value={phone}
                      onChange={e => { setPhone(e.target.value); touchBusiness() }}
                      maxLength={50}
                      disabled={disabled}
                      autoComplete="tel"
                    />
                    {businessFieldErrors.phone && <span className="field-error">{businessFieldErrors.phone}</span>}
                  </div>

                  <div className="form-field">
                    <label htmlFor="settingsEmail">{t('settings.business.email')}</label>
                    <input
                      id="settingsEmail"
                      type="email"
                      value={email}
                      onChange={e => { setEmail(e.target.value); touchBusiness() }}
                      maxLength={255}
                      disabled={disabled}
                      autoComplete="email"
                    />
                    {businessFieldErrors.email && <span className="field-error">{businessFieldErrors.email}</span>}
                  </div>
                </div>

                <div className="form-field">
                  <label htmlFor="settingsTimezone">{t('settings.business.timezone')}</label>
                  <select
                    id="settingsTimezone"
                    value={timezone}
                    onChange={e => { setTimezone(e.target.value); touchBusiness() }}
                    disabled={disabled}
                  >
                    {TIMEZONES.map(tz => <option key={tz} value={tz}>{tz}</option>)}
                  </select>
                </div>
              </>
            )}

            {canManage && (
              <button type="submit" className="btn btn-primary" disabled={businessSave.saving}>
                {businessSave.saving ? t('settings.saving') : t('settings.saveBusiness')}
              </button>
            )}
          </form>
        </section>

        <section className="settings-card" aria-labelledby="settingsAcceptanceTitle">
          <h3 id="settingsAcceptanceTitle">{t('settings.acceptance.title')}</h3>
          <p className="page-section-description">{t('settings.acceptance.subtitle')}</p>

          {acceptanceSave.success && (
            <div className="form-success-banner" role="status">{t('settings.saved')}</div>
          )}
          {acceptanceSave.error && (
            <div className="form-error-banner">{acceptanceSave.error}</div>
          )}

          {!settings.location && (
            <div className="form-error-banner">{t('settings.noLocation')}</div>
          )}

          <form onSubmit={handleAcceptanceSubmit}>
            <label className="settings-toggle">
              <input
                type="checkbox"
                checked={pickupEnabled}
                disabled={acceptanceDisabled}
                onChange={e => {
                  const next = e.target.checked
                  if (!next && !deliveryEnabled) return
                  setPickupEnabled(next)
                  touchAcceptance()
                }}
              />
              <span>
                <strong>{t('settings.acceptance.pickup')}</strong>
                <small>{t('settings.acceptance.pickupHint')}</small>
              </span>
            </label>

            <label className="settings-toggle">
              <input
                type="checkbox"
                checked={deliveryEnabled}
                disabled={acceptanceDisabled}
                onChange={e => {
                  const next = e.target.checked
                  if (!next && !pickupEnabled) return
                  setDeliveryEnabled(next)
                  touchAcceptance()
                }}
              />
              <span>
                <strong>{t('settings.acceptance.delivery')}</strong>
                <small>{t('settings.acceptance.deliveryHint')}</small>
              </span>
            </label>

            <p className="field-hint">{t('settings.acceptance.atLeastOne')}</p>

            {canManage && settings.location && (
              <button type="submit" className="btn btn-primary" disabled={acceptanceSave.saving}>
                {acceptanceSave.saving ? t('settings.saving') : t('settings.saveAcceptance')}
              </button>
            )}
          </form>
        </section>

        <section className="settings-card" aria-labelledby="settingsScheduleTitle">
          <h3 id="settingsScheduleTitle">{t('settings.schedule.title')}</h3>
          <p className="page-section-description">{t('settings.schedule.subtitle')}</p>

          {scheduleSave.success && (
            <div className="form-success-banner" role="status">{t('settings.saved')}</div>
          )}
          {scheduleSave.error && (
            <div className="form-error-banner">{scheduleSave.error}</div>
          )}
          {scheduleFieldError && (
            <div className="form-error-banner">{scheduleFieldError}</div>
          )}

          {!settings.location && (
            <div className="form-error-banner">{t('settings.noLocation')}</div>
          )}

          <form onSubmit={handleScheduleSubmit}>
            <label className="settings-toggle">
              <input
                type="checkbox"
                checked={scheduleConfigured}
                disabled={scheduleDisabled}
                onChange={e => { setScheduleConfigured(e.target.checked); touchSchedule() }}
              />
              <span>
                <strong>{t('settings.schedule.enabled')}</strong>
                <small>{t('settings.schedule.enabledHint')}</small>
              </span>
            </label>

            <div className="settings-days">
              {DAY_ORDER.map(day => {
                const entry = week[day]!
                return (
                  <div key={day} className="settings-day-row">
                    <label className="settings-day-name">
                      <input
                        type="checkbox"
                        checked={entry.enabled}
                        disabled={scheduleDisabled}
                        onChange={e => toggleDay(day, e.target.checked)}
                      />
                      <span>{t(`settings.days.${DAY_KEYS[day]}`)}</span>
                    </label>
                    {entry.enabled ? (
                      <div className="settings-day-intervals">
                        {entry.intervals.map((interval, index) => {
                          const label = `${t(`settings.days.${DAY_KEYS[day]}`)} — ${t('settings.schedule.interval', { index: index + 1 })}`
                          return (
                            <div key={index} className="settings-interval-row">
                              <input
                                type="time"
                                className="settings-time-input"
                                value={interval.openTime}
                                disabled={scheduleDisabled}
                                aria-label={`${label} — ${t('settings.schedule.open')}`}
                                onChange={e => updateInterval(day, index, { openTime: e.target.value })}
                              />
                              <span className="settings-time-separator">—</span>
                              <input
                                type="time"
                                className="settings-time-input"
                                value={interval.closeTime}
                                disabled={scheduleDisabled}
                                aria-label={`${label} — ${t('settings.schedule.close')}`}
                                onChange={e => updateInterval(day, index, { closeTime: e.target.value })}
                              />
                              <button
                                type="button"
                                className="settings-interval-remove"
                                disabled={scheduleDisabled}
                                aria-label={`${label} — ${t('settings.schedule.removeInterval')}`}
                                onClick={() => removeInterval(day, index)}
                              >
                                ×
                              </button>
                            </div>
                          )
                        })}
                        <button
                          type="button"
                          className="settings-interval-add"
                          disabled={scheduleDisabled}
                          onClick={() => addInterval(day)}
                        >
                          + {t('settings.schedule.addInterval')}
                        </button>
                      </div>
                    ) : (
                      <span className="settings-day-off">{t('settings.schedule.dayOff')}</span>
                    )}
                  </div>
                )
              })}
            </div>

            <p className="field-hint">{t('settings.schedule.timezoneHint', { timezone })}</p>
            <p className="field-hint">{t('settings.schedule.overnightHint')}</p>

            {canManage && settings.location && (
              <button type="submit" className="btn btn-primary" disabled={scheduleSave.saving}>
                {scheduleSave.saving ? t('settings.saving') : t('settings.saveSchedule')}
              </button>
            )}
          </form>
        </section>
      </div>
    </section>
  )
}
